import { Label, LabelsByBoards } from '../../../../../../common/labels/labelTypes';

/**
 * Merge the existing and latest 'current labels' lists, while ensuring that only available labels are included and that
 * new labels are added. The only difference from using the latest list directly is that labels that are no longer
 * available due to being removed from the last element will still appear in the list.
 * The allLabels list is used to refresh label data (name/color) for all labels, including preserved ones.
 */
export const mergeCurrentLabels = (
    existingCurrentLabels: LabelsByBoards,
    latestCurrentLabels: LabelsByBoards,
    allLabels: Label[],
): LabelsByBoards => {
    const allLabelsById = new Map(allLabels.map((label) => [label._id, label]));
    const matchesGroup = (group: { id: string }, target: { id: string }) => group.id === target.id;

    const mergedLabelsByBoards = existingCurrentLabels
        .map(({ id, title, labels }) => ({
            id,
            title,
            labels: labels
                .filter((label) => allLabelsById.has(label._id))
                .map((label) => allLabelsById.get(label._id) || label),
        }))
        .filter(({ labels }) => labels.length > 0);

    const mergedLabelIds = new Set(mergedLabelsByBoards.flatMap(({ labels }) => labels.map((label) => label._id)));

    latestCurrentLabels.forEach(({ id, title, labels }) => {
        const mergedGroup = mergedLabelsByBoards.find((group) => matchesGroup(group, { id }));
        const labelsToAdd = labels.filter((label) => allLabelsById.has(label._id) && !mergedLabelIds.has(label._id));

        if (!labelsToAdd.length) return;

        labelsToAdd.forEach((label) => mergedLabelIds.add(label._id));

        if (mergedGroup) {
            mergedGroup.labels = [...mergedGroup.labels, ...labelsToAdd];
            return;
        }

        mergedLabelsByBoards.push({ id, title, labels: labelsToAdd });
    });

    return mergedLabelsByBoards;
};
